/**
 * Accessible Video Player
 * Custom Play/Pause controls for mood videos
 *
 * WCAG 2.1 AA Compliance:
 * - 2.1.1 Keyboard: Full keyboard control (Enter/Space)
 * - 2.4.7 Focus Visible: Button visible when focused
 * - 4.1.2 Name, Role, Value: Proper ARIA labels
 *
 * Behavior:
 * - Single centered button for play/pause
 * - Button fades out during playback
 * - Button visible on hover or when focused
 * - When playing starts, focus moves to video element
 * - Click on video area toggles play/pause
 *
 * @package NWZ-2026
 * @since 2.0
 */

interface VideoPlayerElements {
    container: HTMLElement;
    video: HTMLVideoElement;
    button: HTMLButtonElement;
}

/**
 * Initialize a single video player
 */
const initVideoPlayer = (container: HTMLElement): void => {
    const video = container.querySelector('video');

    if (!video) {
        return;
    }

    // Remove native controls
    video.removeAttribute('controls');

    // Make video focusable for keyboard users
    video.setAttribute('tabindex', '0');

    // Create play/pause button
    const button = createButton();
    container.appendChild(button);

    // Set initial state
    container.classList.add('video-player');
    container.classList.add('video-player--paused');

    const elements: VideoPlayerElements = { container, video, button };

    // Bind events
    bindEvents(elements);
};

/**
 * Create the play/pause button (centered)
 */
const createButton = (): HTMLButtonElement => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'video-player__button';
    button.setAttribute('aria-label', 'Video abspielen');

    button.innerHTML = `
        <svg class="video-player__icon video-player__icon--play" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5v14l11-7z"/>
        </svg>
        <svg class="video-player__icon video-player__icon--pause" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
        </svg>
    `;

    return button;
};

/**
 * Bind all event listeners
 */
const bindEvents = (elements: VideoPlayerElements): void => {
    const { container, video, button } = elements;

    // Button click
    button.addEventListener('click', (e) => {
        e.stopPropagation();
        togglePlayPause(elements);
    });

    // Click on video area (not on button)
    container.addEventListener('click', (e) => {
        const target = e.target as Node;

        if (target === button || button.contains(target)) {
            return;
        }

        togglePlayPause(elements);
    });

    // Keyboard on video element (Space/Enter)
    video.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            togglePlayPause(elements);
        }
    });

    // Video events
    video.addEventListener('play', () => updateState(elements, false));
    video.addEventListener('pause', () => updateState(elements, true));
    video.addEventListener('ended', () => {
        updateState(elements, true);
        // When video ends, move focus back to button
        button.focus();
    });
};

/**
 * Toggle play/pause state
 */
const togglePlayPause = (elements: VideoPlayerElements): void => {
    const { video, button } = elements;

    if (video.paused) {
        video.play();
        // Move focus to video so button can fade out
        video.focus();
    } else {
        video.pause();
        // Move focus to button so user can see it and resume
        button.focus();
    }
};

/**
 * Update visual state and ARIA attributes
 */
const updateState = (elements: VideoPlayerElements, isPaused: boolean): void => {
    const { container, button } = elements;

    if (isPaused) {
        container.classList.add('video-player--paused');
        container.classList.remove('video-player--playing');
        button.setAttribute('aria-label', 'Video abspielen');
    } else {
        container.classList.remove('video-player--paused');
        container.classList.add('video-player--playing');
        button.setAttribute('aria-label', 'Video pausieren');
    }
};

/**
 * Initialize all video players on the page
 */
const initVideoPlayers = (): void => {
    const containers = document.querySelectorAll<HTMLElement>('.event-gallery__video');

    containers.forEach((container) => {
        initVideoPlayer(container);
    });
};

export { initVideoPlayers };
